import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import {
  Rocket,
  BookOpen,
  Bell,
  Presentation,
  Network,
  Globe2,
  Plane,
  Landmark,
  Building,
} from "lucide-react";
import { Card } from "./ui/card";

const opportunities = [
  {
    icon: Rocket,
    title: "Market Entry Strategy",
    description: "Product introduction and market positioning support",
    span: "col-span-1 md:col-span-2",
    color: "from-emerald-500 to-emerald-600",
  },
  {
    icon: BookOpen,
    title: "Knowledge Platform",
    description: "Access to discussions and industry insights",
    span: "col-span-1",
    color: "from-blue-500 to-blue-600",
  },
  {
    icon: Bell,
    title: "Tender Alerts",
    description: "Real-time alerts and tender analysis",
    span: "col-span-1",
    color: "from-amber-500 to-amber-600",
  },
  {
    icon: Presentation,
    title: "Showcase Solutions",
    description: "Platform to display products, solutions & services",
    span: "col-span-1 md:col-span-2",
    color: "from-violet-500 to-violet-600",
  },
  {
    icon: Network,
    title: "Industry Networking",
    description: "Connect with key stakeholders across the industry",
    span: "col-span-1",
    color: "from-rose-500 to-rose-600",
  },
  {
    icon: Globe2,
    title: "Global Presence",
    description: "Leverage international partnerships for expansion",
    span: "col-span-1",
    color: "from-cyan-500 to-cyan-600",
  },
  {
    icon: Plane,
    title: "Trade Delegations",
    description: "International trade delegations and events",
    span: "col-span-1",
    color: "from-teal-500 to-teal-600",
  },
  {
    icon: Landmark,
    title: "Financial Institutions",
    description: "Technology overview engagement with financiers",
    span: "col-span-1 md:col-span-2",
    color: "from-indigo-500 to-indigo-600",
  },
  {
    icon: Building,
    title: "Government Engagement",
    description: "Policy development support with municipalities",
    span: "col-span-1",
    color: "from-emerald-500 to-teal-600",
  },
];

const OpportunitiesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="opportunities"
      ref={ref}
      data-testid="opportunities-section"
      className="py-20 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-800 rounded-full text-sm font-semibold mb-4">
            <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
            Coming Soon
          </div>
          <span className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-700 mb-4 block">
            Member Benefits
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-stone-900 mb-4 tracking-tight">
            Key Opportunities for Members
          </h2>
          <p className="text-stone-600 leading-relaxed">
            T4EM operates on a membership by subscription model, providing a
            holistic ecosystem to network and grow your business in India and
            worldwide.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {opportunities.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.08, duration: 0.5 }}
              className={item.span}
            >
              <Card
                className="bento-card h-full p-6 md:p-8 bg-white border border-stone-200 rounded-2xl hover:border-emerald-200 group cursor-pointer"
                data-testid={`opportunity-card-${index}`}
              >
                <div
                  className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${item.color} mb-4 shadow-lg shadow-${item.color.split("-")[1]}-500/20 group-hover:scale-110 transition-transform duration-300`}
                >
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-stone-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-stone-500 text-sm leading-relaxed">
                  {item.description}
                </p>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default OpportunitiesSection;
